import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { brandsService } from "../../services/brands";
import { audiencesApi } from "../../redux/api/audiencesApi";
import { brandsApi } from "../../redux/api/brandsApi";
import OrbitalLoader from "../common/OrbitalLoader";

const CheckIcon = () => (
  <svg
    className="w-4 h-4 text-green-400 shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const StepSpinner = ({ active = false }) => (
  <span className="inline-flex items-center justify-center w-4 h-4 shrink-0 overflow-visible">
    <OrbitalLoader size={32} special={active} />
  </span>
);

const StepDot = () => (
  <span className="inline-block w-4 h-4 rounded-full border-2 border-theme-secondary shrink-0" />
);

/**
 * Reusable brand population progress UI (steps, timing copy, Skip / View Brand).
 * Used when creating a brand and when resyncing from Settings.
 * When onBeforeNavigateToBrand is provided (resync flow), we call it when steps complete or when user clicks View Brand/Skip so the Overview can show fresh data.
 */
export function BrandPopulationProgress({
  brandHash,
  brandUrl,
  onBeforeNavigateToBrand,
  onContinueClick,
  continueLabel,
  onFinished,
  onSkipClick,
  skipLabel,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const projectHash = useSelector((state) => state.project.currentProject?.hash);
  const [data, setData] = useState(null);
  const initialDeepRef = useRef(null);
  const hasTriggeredRefetchRef = useRef(false);
  const hasTriggeredFinishedRef = useRef(false);
  const hasTriggeredCacheInvalidateRef = useRef(false);
  const timeoutRef = useRef(null);

  const POLL_INTERVAL_MS = 3000;

  useEffect(() => {
    if (!brandHash) return;

    let cancelled = false;

    const pollStatus = () => {
      brandsService.getPopulationStatus(
        brandHash,
        (response) => {
          if (cancelled) return;

          const d = response.data;
          if (initialDeepRef.current === null && d != null) {
            initialDeepRef.current = Boolean(d.deep);
          }
          setData(d);

          const done = d?.status === "completed" || d?.status === "failed";
          if (!done) {
            timeoutRef.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
          }
        },
        () => {
          if (cancelled) return;
          setData((prev) => prev);
        },
      );
    };

    pollStatus();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  }, [brandHash]);

  const steps = data?.steps || [];
  const isCompleted = data?.status === "completed";
  const hasFailed = data?.status === "failed";
  const isFinished = isCompleted || hasFailed;
  const hasAnyStepDone = steps.length > 0 && steps.some((s) => s.done);

  const findActiveIdx = () => {
    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].done) return i;
    }
    return -1;
  };
  const activeIdx = findActiveIdx();

  // When used from resync flow, refetch brand once when steps complete so Overview will have latest data
  useEffect(() => {
    if (
      onBeforeNavigateToBrand &&
      isFinished &&
      !hasTriggeredRefetchRef.current
    ) {
      hasTriggeredRefetchRef.current = true;
      onBeforeNavigateToBrand();
    }
  }, [onBeforeNavigateToBrand, isFinished]);

  useEffect(() => {
    if (onFinished && isFinished && !hasTriggeredFinishedRef.current) {
      hasTriggeredFinishedRef.current = true;
      onFinished();
    }
  }, [onFinished, isFinished]);

  useEffect(() => {
    if (!isCompleted || !projectHash || hasTriggeredCacheInvalidateRef.current) {
      return;
    }
    hasTriggeredCacheInvalidateRef.current = true;
    dispatch(brandsApi.util.invalidateTags([{ type: "Brands", id: "LIST" }]));
    dispatch(
      audiencesApi.util.invalidateTags([{ type: "Audiences", id: projectHash }]),
    );
  }, [dispatch, isCompleted, projectHash]);

  const handleNavClick = async (event) => {
    if (!onBeforeNavigateToBrand) return;
    event.preventDefault();
    await Promise.resolve(onBeforeNavigateToBrand());
    navigate(brandUrl);
  };

  return (
    <>
      <h2 className="text-2xl font-semibold text-theme-text mb-1">
        {isCompleted
          ? "Brand Ready"
          : hasFailed
            ? "Population Failed"
            : "Setting Up Brand"}
      </h2>
      <p className="text-sm text-theme-text opacity-60 mb-6">
        {isCompleted
          ? "All steps completed successfully."
          : hasFailed
            ? "Something went wrong. You can still view partial results."
            : (initialDeepRef.current ?? data?.deep)
              ? "This usually takes 60-90 seconds."
              : "This usually takes 15-30 seconds."}
      </p>

      <div className="space-y-3 mb-8">
        {steps.map((step, idx) => {
          const isActive = idx === activeIdx && !isFinished;
          return (
            <div
              key={step.key}
              className={`flex items-center gap-3 text-sm ${
                step.done
                  ? "text-theme-text"
                  : isActive
                    ? "text-theme-text"
                    : "text-theme-text opacity-40"
              }`}
            >
              {step.done ? (
                <CheckIcon />
              ) : isActive ? (
                <StepSpinner active />
              ) : (
                <StepDot />
              )}
              <span className="flex-1">{step.label}</span>
              {step.count != null && (
                <span className="text-xs opacity-60 tabular-nums">
                  {step.count}
                </span>
              )}
            </div>
          );
        })}
        {!data && (
          <div className="flex items-center gap-3 text-sm text-theme-text">
            <StepSpinner active />
            <span>Initializing...</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-h-[2.25rem] flex items-center">
          {onSkipClick && !isFinished ? (
            <button
              type="button"
              onClick={onSkipClick}
              className="text-sm text-theme-text opacity-70 hover:opacity-100 underline"
            >
              {skipLabel || "Skip"}
            </button>
          ) : null}
        </div>
        <div className="flex justify-end gap-3">
          {isFinished ? (
            onContinueClick ? (
              <button
                type="button"
                onClick={onContinueClick}
                className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
              >
                {continueLabel || "Continue"}
              </button>
            ) : (
              <Link
                to={brandUrl}
                onClick={handleNavClick}
                className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
              >
                View Brand
              </Link>
            )
          ) : (
            <span className="px-4 py-2 bg-theme-primary text-theme-button rounded-md opacity-50 cursor-not-allowed">
              {onContinueClick ? continueLabel || "Continue" : "View Brand"}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
